import { Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEntityFormSubmit } from '@scoring/logic';
import { UserRole } from '@scoring/types';
import { Button, EditorProvider, ModalEditor, ModalEditorItem, useAutoDisclosure } from '@scoring/ui';
import { IconCircleDashedCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { trySetFormApiErrors } from '../form';
import { useActiveUser, type ActiveUserForm, type PasswordChangeForm } from './useActiveUser';

interface ActiveUserModalProps {
    opened: boolean;
    onClose: () => void;
}

export const ActiveUserModal: React.FC<ActiveUserModalProps> = ({ opened, onClose }) => {
    const { validate, validatePasswordChange, user, update, changePassword } = useActiveUser();
    const [loadingPasswordChange, setLoadingPasswordChange] = useState(false);
    const { opened: success, toggle: toggleSuccess } = useAutoDisclosure();

    const form = useForm<ActiveUserForm>({
        mode: 'uncontrolled',
        validateInputOnChange: false,
        validateInputOnBlur: true,
        initialValues: user,
        validate: validate,
    });

    const formPasswordChange = useForm<PasswordChangeForm>({
        mode: 'uncontrolled',
        initialValues: { password: '', passwordConfirm: '' },
        validateInputOnBlur: false,
        validate: validatePasswordChange,
    });

    const [prevOpened, setPrevOpened] = useState(opened);
    if (opened !== prevOpened) {
        setPrevOpened(opened);
        if (opened) {
            formPasswordChange.reset();
        }
    }

    const { submit, loading } = useEntityFormSubmit<ActiveUserForm>({
        form,
        onUpdate: async (values) => {
            await update(values);
        },
    });

    const handleSave = async () => {
        console.log('handleSave');
        await submit();
    };

    const handleChangePassword = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        formPasswordChange.validate();
        if (formPasswordChange.isValid()) {
            try {
                setLoadingPasswordChange(true);
                await changePassword(formPasswordChange.getValues());
                toggleSuccess();
                formPasswordChange.reset();
            } catch (error) {
                trySetFormApiErrors(error, formPasswordChange);
            } finally {
                setLoadingPasswordChange(false);
            }
        } else {
            console.log('Form is invalid:', form.errors);
        }
    };

    const handleSaveEmpty = async () => {
        // No-op - form submission is handled by handleSubmit
    };

    return (
        <ModalEditor
            opened={opened}
            onClose={onClose}
            title="Account Settings"
            description="Manage your user account"
            size="lg"
        >
            <Stack gap="md">
                <Group>
                    <Text size="sm">{user!.email}</Text>
                    {user?.roles
                        .filter((role) => role !== UserRole.User)
                        .map((role) => (
                            <Badge key={role} variant="light">
                                {role}
                            </Badge>
                        ))}
                </Group>
                <Paper p="md" withBorder>
                    <Stack>
                        <form>
                            <EditorProvider form={form} mode="update" onSave={handleSave} loading={loading}>
                                <Stack gap="xs">
                                    <ModalEditorItem.Text fieldKey="firstName" label="First Name" />
                                    <ModalEditorItem.Text fieldKey="lastName" label="Last Name" />
                                    <ModalEditorItem.Country fieldKey="iocCountryCode" />
                                    <ModalEditorItem.Text fieldKey="organisation" label="Organisation" />
                                </Stack>
                            </EditorProvider>
                        </form>
                    </Stack>
                </Paper>
                <Paper p="md" withBorder>
                    <Stack>
                        <Group>
                            {' '}
                            <Text size="sm">Change Password</Text>
                            {success && <IconCircleDashedCheck size={18} />}
                        </Group>

                        <form
                            onSubmit={handleChangePassword}
                            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                        >
                            <EditorProvider
                                form={formPasswordChange}
                                mode="update"
                                onSave={handleSaveEmpty}
                                loading={loadingPasswordChange}
                            >
                                <Stack gap="xs">
                                    <ModalEditorItem.Password placeholder="Password" fieldKey="password" />
                                    <ModalEditorItem.Password
                                        placeholder="ConfirmPassword"
                                        fieldKey="passwordConfirm"
                                    />
                                    <Button type="submit" variant="filled">
                                        Change Password
                                    </Button>
                                </Stack>
                            </EditorProvider>
                        </form>
                    </Stack>
                </Paper>
            </Stack>
        </ModalEditor>
    );
};
